import React, { useCallback, useRef, useState } from "react";
import equal from "fast-deep-equal/es6";
import { observer } from "mobx-react";

import { combineEventFilters } from "@formant/core/common/combineFilters";
import { sheetExportMaxRowCount } from "@formant/core/common/config";
import { delay } from "@formant/core/common/delay";
import { plural } from "@formant/core/common/plural";
import { duration } from "@formant/core/model/duration";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventQuery } from "@formant/core/model/IEventQuery";
import { IFilter } from "@formant/core/model/IFilter";
import { Fader } from "@formant/ui/component/common/Fader";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { VStack } from "@formant/ui/component/common/VStack";

import { track } from "../../analytics";
import { eventClient } from "../../clients";
import { useStores } from "../../context/StoreContext";
import { notificationEventTypes } from "../../store/SeekOption";
import { Buttons } from "../common/Buttons";
import { ButtonsBar } from "../common/ButtonsBar";
import { FlexSpacer } from "../common/FlexSpacer";
import { Heading1 } from "../common/Heading1";
import { Pagination } from "../common/Pagination";
import { Popup } from "../common/Popup";
import { StandardButton } from "../common/StandardButton";
import { Switch } from "../common/Switch";
import { ErrorPageError } from "../ErrorPageError";
import { EventStorePoller } from "../Overview/StorePollers";
import { EventListItem } from "./EventListItem";
import { IEventListState } from "./EventListState";
import styles from "./index.scss";

export interface IEventListProps {
    minimal?: boolean;
    eventListState: IEventListState;
    context: IFilter;
    onChange: (eventListState: IEventListState) => void;
    onSelect: (event: IEvent) => void;
    onSelectDeviceRequired?: boolean;
    pollForNewEvents?: boolean;
}

const perPage = 50;
const initialEventList: IEvent[] = [];

export const EventList = observer(function EventList(props: IEventListProps) {
    const {
        context,
        eventListState,
        minimal,
        onChange,
        onSelect,
        onSelectDeviceRequired,
        pollForNewEvents = false,
    } = props;
    const { filter, page, sort } = eventListState;

    const { eventStore, authStore, organizationStore, routerStore } =
        useStores();

    const [markAsReadPending, setMarkAsReadPending] = useState(false);
    const [largeUpdatePending, setLargeUpdatePending] = useState(false);
    const [exportPending, setExportPending] = useState(false);
    const previousEvents = useRef<IEvent[]>(initialEventList);

    const compositeFilter = combineEventFilters(context, {
        ...filter,
        disableNullMatches: true,
        ...("notificationEnabled" in filter
            ? { notificationEnabled: filter.notificationEnabled }
            : { notificationEnabled: true }),
        ...("viewed" in filter ? { viewed: filter.viewed } : { viewed: false }),
        ...("eventTypes" in filter
            ? { eventTypes: filter.eventTypes }
            : {
                  eventTypes: notificationEventTypes,
              }),
    });

    const showOnlyNotifications =
        compositeFilter.eventTypes !== undefined &&
        equal(
            new Set(compositeFilter.eventTypes),
            new Set(notificationEventTypes),
        );

    const query: IEventQuery = {
        ...compositeFilter,
        offset: page * perPage,
        count: perPage,
        sort,
    };

    previousEvents.current = eventStore.query(query) || previousEvents.current;
    const events = previousEvents.current;

    const count = eventStore.count(compositeFilter);

    const loading = events === undefined || count === undefined;

    const initialLoad = previousEvents.current === initialEventList;

    const noResults = count === 0;

    const unviewedCount =
        eventStore.count({
            ...compositeFilter,
            viewed: false,
        }) || 0;

    const deviceImplied = context.deviceIds?.length === 1;

    const exportEventCount = Math.min(sheetExportMaxRowCount, count || 0);

    const eventItems = events.map(event => {
        // Prevent selection of rows when there is nowhere to go because
        // there is no channel in context and the event has no device ID.
        const onSelectItem =
            ((!onSelectDeviceRequired || event.deviceId) && onSelect) ||
            undefined;

        return (
            <EventListItem
                key={event.id}
                event={event}
                filter={filter}
                onSelect={onSelectItem}
                hideDeviceColumn={deviceImplied}
                minimal={minimal}
            />
        );
    });

    const showMoreEvents = useCallback(() => {
        const { navigate, routeTo } = routerStore;
        void navigate(routeTo.events());
    }, []);

    const onToggleShowUnread = useCallback(
        (showUnreadOnly: boolean) => {
            onChange({
                ...eventListState,
                filter: {
                    ...eventListState.filter,
                    viewed: showUnreadOnly ? false : undefined,
                },
                page: 0,
            });
        },
        [eventListState, onChange],
    );

    const onToggleOnlyNotifications = useCallback(
        (onlyNotifications: boolean) => {
            onChange({
                ...eventListState,
                filter: combineEventFilters(eventListState.filter, {
                    eventTypes: onlyNotifications
                        ? notificationEventTypes
                        : undefined,
                    ...(!onlyNotifications && {
                        notificationEnabled: undefined,
                    }),
                    ...("viewed" in filter
                        ? { viewed: filter.viewed }
                        : { viewed: false }),
                }),
                page: 0,
            });
        },
        [onChange, combineEventFilters, eventListState, filter],
    );

    const windowRef = useRef<HTMLDivElement | null>(null);

    const onChangePage = useCallback(
        (nextPage: number) => {
            onChange({ ...eventListState, page: nextPage });
            windowRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        },
        [eventListState, onChange],
    );

    const onExport = useCallback(async () => {
        const exportFilter = combineEventFilters(
            context,
            eventListState.filter,
        );

        setExportPending(true);
        try {
            const url = await eventClient.exportSheet(
                await authStore.token(),
                exportFilter,
                window.location.origin,
            );
            window.open(url, "_blank");
            track("Export Sheet");
        } catch (error) {
            throw new ErrorPageError(
                error as Error,
                "Error exporting events. Please try to disconnect and reconnect Google Sheets integration.",
            );
        } finally {
            setExportPending(false);
        }
    }, [authStore, context, eventClient, eventListState]);

    const onMarkAsRead = useCallback(async () => {
        const markAsReadFilter = combineEventFilters(
            context,
            eventListState.filter,
        );

        setLargeUpdatePending(false);
        setMarkAsReadPending(true);
        let eventCompleted = false;
        try {
            while (!eventCompleted) {
                const { completed } = await eventStore.view(markAsReadFilter);
                eventCompleted = completed;
                if (!eventCompleted) {
                    setLargeUpdatePending(true);
                    await delay(1 * duration.second);
                    // continue marking more events as read
                }
            }
        } finally {
            setLargeUpdatePending(false);
            setMarkAsReadPending(false);
        }
        onChange({
            ...eventListState,
            page: 0,
        });
    }, [context, eventListState, eventStore, onChange]);

    const onCancelUpdate = useCallback(() => {
        setLargeUpdatePending(false);
        setMarkAsReadPending(false);
    }, []);

    return (
        <Fader visible={!initialLoad} className={styles["event-list"]}>
            {pollForNewEvents && <EventStorePoller query={query} />}
            {noResults && (
                <div className={styles["no-unread-results"]}>
                    <div className={styles["no-unread-container"]}>
                        <Heading1 className={styles.heading}>
                            No matching events
                        </Heading1>
                    </div>
                </div>
            )}
            {!noResults && (
                <VStack ref={windowRef} className={styles.list}>
                    {eventItems}
                </VStack>
            )}
            {minimal ? (
                <StandardButton
                    className={styles["show-more"]}
                    onClick={showMoreEvents}
                >
                    Show More
                </StandardButton>
            ) : (
                <ButtonsBar
                    className={styles.footer}
                    extra={
                        <div className={styles.actions}>
                            <div style={{ display: "flex", gap: "21px" }}>
                                <Switch
                                    className={styles["show-unread"]}
                                    state={showOnlyNotifications}
                                    onChange={onToggleOnlyNotifications}
                                    label="Notifications only"
                                />

                                <Switch
                                    className={styles["show-unread"]}
                                    state={compositeFilter.viewed === false}
                                    onChange={onToggleShowUnread}
                                    label="Unread only"
                                />
                            </div>
                            {count !== undefined && (
                                <Pagination
                                    page={page}
                                    total={count}
                                    perPage={perPage}
                                    onChange={onChangePage}
                                    className={styles.pagination}
                                />
                            )}
                        </div>
                    }
                >
                    <StandardButton
                        onClick={onExport}
                        data-tooltip={
                            organizationStore.isGoogleSheetsEnabled
                                ? null
                                : "Google Sheets integration needs to be enabled."
                        }
                        disabled={
                            exportEventCount === 0 ||
                            !organizationStore.isGoogleSheetsEnabled
                        }
                        className={styles.action}
                        loading={exportPending}
                    >
                        Export{" "}
                        {exportEventCount === sheetExportMaxRowCount
                            ? "first "
                            : ""}
                        {exportEventCount}{" "}
                        {plural("notification", exportEventCount || 0)}
                    </StandardButton>
                    <StandardButton
                        onClick={onMarkAsRead}
                        disabled={
                            unviewedCount === 0 ||
                            !authStore.hasAccess("events", "operator")
                        }
                        className={styles.action}
                        loading={markAsReadPending}
                    >
                        Mark {unviewedCount} as read
                    </StandardButton>
                </ButtonsBar>
            )}

            <Popup
                visible={largeUpdatePending}
                className={styles["large-update-popup"]}
            >
                <Heading1>Updating...</Heading1>
                <p>
                    There are a large number of unread notifications. This may
                    take a while.
                </p>
                <Buttons className={styles.actions}>
                    <FlexSpacer />
                    <StandardButton onClicked={onCancelUpdate}>
                        Cancel
                    </StandardButton>
                </Buttons>
            </Popup>

            <LoadingIndicator visible={loading} />
        </Fader>
    );
});
